import type { TokenPath, TokenOverride } from './types.js';

/** A single token entry from the manifest. */
export interface ManifestToken {
  cssVar: string;
  value: string;
  type: string;
}

/** A group of tokens from a single CRD source. */
export interface ManifestGroup {
  source: string;
  tokens: Record<string, ManifestToken>;
}

/** The full tokens-manifest.json structure. */
export interface TokenManifest {
  version: string;
  generated: string;
  tokenCount: number;
  groups: Record<string, ManifestGroup>;
}

/** A resolved token: manifest entry + path info. */
export interface ResolvedToken {
  path: TokenPath;
  value: string;
  type: string;
}

/**
 * Runtime registry for design tokens.
 *
 * Loads from the tokens-manifest.json generated by @bq/web-tokens and provides
 * lookup, search, and override resolution.
 */
export class TokenRegistry {
  private readonly tokensByVar = new Map<string, ResolvedToken>();
  private readonly groupMap = new Map<string, ResolvedToken[]>();
  private readonly allTokens: ResolvedToken[] = [];

  constructor(manifest: TokenManifest) {
    for (const [groupName, group] of Object.entries(manifest.groups)) {
      const groupTokens: ResolvedToken[] = [];

      for (const [key, entry] of Object.entries(group.tokens)) {
        const resolved: ResolvedToken = {
          path: { group: groupName, key, cssVar: entry.cssVar },
          value: entry.value,
          type: entry.type,
        };
        this.tokensByVar.set(entry.cssVar, resolved);
        groupTokens.push(resolved);
        this.allTokens.push(resolved);
      }

      this.groupMap.set(groupName, groupTokens);
    }
  }

  /** Get a token by its CSS variable name (e.g. "--bq-p500"). */
  getToken(cssVar: string): ResolvedToken | undefined {
    return this.tokensByVar.get(cssVar);
  }

  /** Get all tokens in a group (e.g. "colors"). */
  getGroup(groupName: string): readonly ResolvedToken[] {
    return this.groupMap.get(groupName) ?? [];
  }

  /** Get all group names. */
  getGroupNames(): string[] {
    return Array.from(this.groupMap.keys());
  }

  /** Get all tokens across all groups. */
  getAllTokens(): readonly ResolvedToken[] {
    return this.allTokens;
  }

  /** Total number of tokens. */
  get size(): number {
    return this.allTokens.length;
  }

  /**
   * Search tokens by substring match on cssVar, group, or key.
   * Case-insensitive.
   */
  search(query: string): ResolvedToken[] {
    const q = query.toLowerCase();
    return this.allTokens.filter(
      (t) =>
        t.path.cssVar.toLowerCase().includes(q) ||
        t.path.group.toLowerCase().includes(q) ||
        t.path.key.toLowerCase().includes(q),
    );
  }

  /**
   * Resolve the effective value for a token, considering overrides.
   * Returns the override value if one exists, otherwise the original manifest value.
   */
  resolveValue(
    cssVar: string,
    overrides: ReadonlyMap<string, TokenOverride>,
  ): string | undefined {
    const override = overrides.get(cssVar);
    if (override) return override.value;
    return this.tokensByVar.get(cssVar)?.value;
  }

  /**
   * Generate a CSS override object for inline styles.
   * Returns a Record mapping cssVar → overridden value.
   */
  toCSSOverrides(overrides: ReadonlyMap<string, TokenOverride>): Record<string, string> {
    const result: Record<string, string> = {};
    for (const [cssVar, override] of overrides) {
      result[cssVar] = override.value;
    }
    return result;
  }
}
